import { ReactNode, useMemo, useState } from "react";

import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  Building2,
  Calendar1,
  Clipboard,
  Clock4,
  DollarSign,
  HandCoins,
  Linkedin,
  LucideIcon,
  MonitorSmartphone,
  ReceiptText,
  Users,
  X,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar } from "@fluentui/react-components";

import OpportunityApplyDialog from "@/components/forms/candidate/opportunity/apply";
import { usePaymentInfoDialogState } from "@/components/dialogs/payment-info/store";
import OpportunityCounterOfferForm from "@/components/forms/offer/counter";
import { DeclineOpportunityDialog } from "./decline";

import { useStripeAccountQuery } from "@/queries/stripe";

import { useAcceptOffer } from "@/mutations/opportunity/accept-offer";
import { useDeclineOffer } from "@/mutations/opportunity/decline-offer";

import dateStringToLocalDate from "@/utils/dateStringToLocalDate";
import formatBudgetRange from "@/utils/formatBudgetRange";

import { CandidateJobOpening, IconProp, Offer, OfferTerm } from "@/types";

interface BaseProps {
  opening: CandidateJobOpening;
}

// Parent may directly control dialog open state, but does not have to
type ControlProps =
  | {
      open?: never;
      setOpen?: never;
    }
  | {
      open: boolean;
      setOpen: (v: boolean) => void;
    };

type Props = BaseProps & ControlProps;

export const OpportunityDetailsDialog = ({
  opening,
  open: externalOpen,
  setOpen: setExternalOpen,
}: Props) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [openApplication, setOpenApplication] = useState(false);
  const [openCounter, setOpenCounter] = useState(false);

  const [open, setOpen] =
    externalOpen !== undefined && setExternalOpen
      ? [externalOpen, setExternalOpen]
      : [internalOpen, setInternalOpen];

  const offer = opening.candidateStatus?.offers.at(-1);

  const canApply = useMemo(
    () =>
      opening.status !== "Role Filled (won)" &&
      opening.status !== "Role Filled (lost)" &&
      (!opening.candidateStatus ||
        opening.candidateStatus.status === "Checking Interest"),
    [opening],
  );

  const canWithdraw = useMemo(
    () =>
      !offer &&
      opening.status !== "Role Filled (won)" &&
      opening.status !== "Role Filled (lost)" &&
      opening.candidateStatus &&
      opening.candidateStatus.status !== "Rejected for Role" &&
      opening.candidateStatus.status !== "Not Interested" &&
      opening.candidateStatus.status !== "Contracting" &&
      opening.candidateStatus.status !== "Accepted by Client",
    [opening, offer],
  );

  const formattedBudget =
    formatBudgetRange(opening.budgetMinCents, opening.budgetMaxCents) ??
    opening.budgetNotes;

  let budgetString: string | undefined;
  if (!formattedBudget) {
    // No-op
  } else if (opening.type === "Project" && opening.budgetType) {
    budgetString = `${formattedBudget} / ${opening.budgetType === "Monthly retainer" ? "mo retainer" : "project"}`;
  } else if (opening.type === "Fractional") {
    budgetString = `${formattedBudget} / mo`;
  } else if (opening.type === "Full-time") {
    budgetString = `${formattedBudget} / yr`;
  }

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        {externalOpen === undefined && !setExternalOpen ? (
          <SheetTrigger asChild>
            <Button variant="secondary">View Details</Button>
          </SheetTrigger>
        ) : null}
        <SheetContent className="w-full max-w-full sm:max-w-md md:max-w-[50%] bg-[#fafafa] line-clamp">
          {openCounter ? (
            <OfferCounterForm
              opening={opening}
              offer={offer}
              closeFn={() => setOpenCounter(false)}
            />
          ) : (
            <>
              <SheetTitle className="pb-4">{opening.role}</SheetTitle>
              <SheetDescription className="sr-only">
                Additional details about this opportunity
              </SheetDescription>
              <ScrollArea className="h-[calc(100vh-4.75rem)]" type="auto">
                <div className="flex flex-col gap-5">
                  <Tile>
                    <div className="flex gap-3 self-stretch items-center">
                      <Avatar
                        image={{ src: opening.company?.logoLink }}
                        name={opening.company?.name}
                        size={48}
                      />
                      <p className="text-lg font-normal text-[#727E94]">
                        {opening.company?.name}
                      </p>
                    </div>
                    {!offer && (
                      <>
                        <div className="flex flex-col md:flex-row gap-x-6 gap-y-2 self-stretch flex-wrap">
                          <OpportunityKeyPoint
                            Icon={Clock4}
                            value={[
                              opening.scopeTime,
                              opening.scopeEngagementDuration,
                            ]
                              .filter((v) => Boolean(v))
                              .join(" | ")}
                          />
                          <OpportunityKeyPoint
                            Icon={Calendar1}
                            value={dateStringToLocalDate(
                              opening.desiredStartDate,
                            )?.toLocaleDateString("en-US", {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            })}
                          />
                          <OpportunityKeyPoint
                            Icon={DollarSign}
                            value={budgetString}
                          />
                        </div>
                      </>
                    )}
                    {opening.budgetNotes &&
                      opening.budgetNotes !== formattedBudget && (
                        <div className="flex flex-row gap-2 items-center">
                          <span className="text-sm text-muted-foreground">
                            Compensation Note: {opening.budgetNotes}
                          </span>
                        </div>
                      )}
                    {!offer &&
                    opening.candidateStatus &&
                    opening.candidateStatus.status !== "Checking Interest" ? (
                      <Button
                        className="w-full"
                        onClick={() =>
                          window.open(
                            `/opening/${opening.id}/application/${opening.candidateStatus!.id}`,
                            "_blank",
                          )
                        }
                      >
                        Preview Application
                      </Button>
                    ) : null}
                    <OfferReview
                      opening={opening}
                      offer={offer}
                      setCounterOpenFn={() => setOpenCounter(true)}
                    />
                    {canApply && (
                      <Button
                        className="w-full"
                        variant="secondary"
                        onClick={() => setOpenApplication(true)}
                      >
                        <Clipboard />
                        Apply
                      </Button>
                    )}
                    {canWithdraw && (
                      <DeclineOpportunityDialog
                        openingId={opening.id}
                        candidateStatusId={opening.candidateStatus!.id}
                        button={
                          <Button className="w-full" variant="outline">
                            {opening.candidateStatus!.status ===
                            "Checking Interest"
                              ? "Decline"
                              : "Withdraw"}
                          </Button>
                        }
                      />
                    )}
                  </Tile>
                  {Boolean(opening.shortDescription) && (
                    <Tile>
                      <h2 className="text-lg font-semibold text-[#181D27]">
                        Job Details
                      </h2>
                      <p className="text-sm font-normal text-[#374151] whitespace-pre-line">
                        {opening.shortDescription}
                      </p>
                    </Tile>
                  )}
                  {Boolean(opening.deliverables) && (
                    <Tile>
                      <h2 className="text-lg font-semibold text-[#181D27]">
                        Deliverables
                      </h2>
                      <p className="text-sm font-normal text-[#374151] whitespace-pre-line">
                        {opening.deliverables}
                      </p>
                    </Tile>
                  )}

                  <Tile>
                    <h2 className="text-lg font-semibold text-[#181D27]">
                      About The Company
                    </h2>
                    <p className="text-sm font-normal text-[#374151] whitespace-pre-line">
                      {opening.company?.aboutUs}
                    </p>
                    <div className="p-4 flex flex-col gap-2 self-stretch border border-[#F2F2F2] rounded-xl">
                      <div className="flex flex-col self-stretch">
                        <BackgroundItem
                          Icon={ReceiptText}
                          name="Founded"
                          value={opening.company?.foundedYear}
                        />
                        <BackgroundItem
                          Icon={Users}
                          name="Company Size"
                          value={opening.company?.companySize}
                        />
                        <BackgroundItem
                          Icon={DollarSign}
                          name="Funds Raised"
                          value={opening.company?.fundsRaised}
                        />
                        <BackgroundItem
                          Icon={HandCoins}
                          name="Last Fundraise"
                          value={opening.company?.lastFundraise}
                        />
                      </div>
                    </div>
                  </Tile>

                  <Tile>
                    <h2 className="text-lg font-semibold text-[#181D27]">
                      Company Links
                    </h2>
                    <div className="flex gap-2 self-stretch flex-wrap">
                      <LinkButton
                        Icon={Linkedin}
                        text="Linkedin"
                        link={opening.company?.linkedIn}
                      />
                      <LinkButton
                        Icon={MonitorSmartphone}
                        text="Website"
                        link={opening.company?.websiteLink}
                      />
                      <Button
                        variant="secondary"
                        onClick={() =>
                          window.open(
                            `/company/${opening.company?.id}`,
                            "_blank",
                          )
                        }
                      >
                        <Building2 />
                        Company Profile
                      </Button>
                    </div>
                  </Tile>
                </div>
              </ScrollArea>
            </>
          )}
          <OpportunityApplyDialog
            opening={opening}
            open={openApplication}
            setOpen={setOpenApplication}
          />
        </SheetContent>
      </Sheet>
    </>
  );
};

const OfferReview = ({
  offer,
  opening,
  setCounterOpenFn,
}: {
  opening: CandidateJobOpening;
  offer?: Offer;
  setCounterOpenFn: () => void;
}) => {
  const { stripeAccount } = useStripeAccountQuery();

  const { acceptOffer, isAcceptingOffer } = useAcceptOffer();
  const { declineOffer, isDecliningOffer } = useDeclineOffer();

  if (!offer) {
    return;
  }

  const offerSenderWasCompany = offer.sender === "company";
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const companyName = opening.company?.name || "the company";

  return (
    <div className="p-6 self-stretch flex flex-col gap-4 border border-brand-primary-200 bg-[#fbfcfb] rounded-xl">
      <div className="flex flex-col self-stretch items-start gap-5">
        <div
          className={cn(
            "inline-flex py-1 px-2 justify-center rounded-[20px]",
            offerSenderWasCompany
              ? "bg-brand-success-600"
              : "bg-brand-pending-600",
            offer.status === "rejected" && "bg-brand-warning-600",
          )}
        >
          <span className="text-sm text-white">
            {offer.status === "accepted"
              ? "Offer accepted"
              : offer.status === "rejected"
                ? "Offer rejected"
                : offerSenderWasCompany
                  ? "Offer received"
                  : "Offer sent"}
          </span>
        </div>
        <h3 className="text-xl text-[#2E2E38] font-bold">
          {offer.status === "accepted"
            ? `Your agreement with ${companyName}`
            : `Review your offer from ${companyName}`}
        </h3>
        {offer.terms.length > 1 && (
          <h3 className="text-xl text-[#2E2E38] font-bold">Offer terms</h3>
        )}
        {offer.terms.map((term, idx) => (
          <OfferTermSection key={`term-${idx}`} term={term} />
        ))}
        {offer.note && (
          <div className="flex flex-col gap-1">
            <p className="text-base text-[#2E2E38] font-semibold">
              A note from {offerSenderWasCompany ? "the employer:" : "you:"}
            </p>
            <p className="text-base text-brand-neutral-black-8">{offer.note}</p>
          </div>
        )}
      </div>
      {offerSenderWasCompany && offer.status === "sent" && (
        <div className="flex flex-row self-stretch justify-end gap-4">
          <Button
            variant="ghost"
            type="submit"
            className="mt-2"
            disabled={isDecliningOffer || isAcceptingOffer}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={() =>
              declineOffer({
                openingId: opening.id,
                offerId: offer.id,
              })
            }
          >
            <X size={12} />
            Decline
          </Button>
          <Button
            variant="stroke"
            type="submit"
            className="mt-2"
            disabled={isDecliningOffer || isAcceptingOffer}
            onClick={setCounterOpenFn}
          >
            Counter offer
          </Button>
          <Button
            variant="success"
            type="submit"
            className="mt-2"
            disabled={isDecliningOffer || isAcceptingOffer}
            onClick={() =>
              acceptOffer({
                openingId: opening.id,
                offerId: offer.id,
              })
            }
          >
            Accept Offer
          </Button>
        </div>
      )}
      {offer.status === "accepted" ? (
        stripeAccount?.status === "ready" ? (
          <div className="flex flex-row self-stretch justify-end gap-4">
            <Button
              variant="success"
              type="submit"
              className="mt-2"
              disabled={isDecliningOffer || isAcceptingOffer}
              onClick={() => usePaymentInfoDialogState.getState().setOpen(true)}
            >
              Review direct deposit
            </Button>
          </div>
        ) : (
          <div className="flex flex-row self-stretch justify-end gap-4">
            <Button
              variant="success"
              type="submit"
              className="mt-2"
              disabled={isDecliningOffer || isAcceptingOffer}
              onClick={() => usePaymentInfoDialogState.getState().setOpen(true)}
            >
              Set up direct deposit
            </Button>
          </div>
        )
      ) : undefined}
    </div>
  );
};

const OfferTermSection = ({ term }: { term: OfferTerm }) => {
  if (term.termType === "milestone") {
    return (
      <div className="flex items-start flex-col gap-3">
        <div className="flex gap-1">
          <p className="text-base text-[#2E2E38] font-semibold">Milestone:</p>
          <p className="text-base text-brand-neutral-black-8">
            {term.milestoneLabel ?? "Unnamed"}
          </p>
        </div>
        <div className="flex gap-1">
          <p className="text-base text-[#2E2E38] font-semibold">Fee:</p>
          <p className="text-base text-brand-neutral-black-8">
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(term.totalAmountCents / 100)}
          </p>
        </div>
      </div>
    );
  } else if (term.termType === "recurring") {
    return (
      <div className="flex items-start gap-10">
        <div className="flex flex-col gap-1">
          <p className="text-base text-[#2E2E38] font-semibold">
            Contract duration:
          </p>
          <p className="text-base text-brand-neutral-black-8">
            {term.iterations} {term.iterations === 1 ? "month" : "months"}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-base text-[#2E2E38] font-semibold">
            Monthly retainer:
          </p>
          <p className="text-base text-brand-neutral-black-8">
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(term.totalAmountCents / 100)}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-base text-[#2E2E38] font-semibold">
            Payment cycle:
          </p>
          <p className="text-base text-brand-neutral-black-8">
            {term.billingDay} of each month
          </p>
        </div>
      </div>
    );
  } else {
    return (
      <div className="flex items-start gap-10">
        <div className="flex flex-col gap-1">
          <p className="text-base text-[#2E2E38] font-semibold">Salary:</p>
          <p className="text-base text-brand-neutral-black-8">
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(term.totalAmountCents / 100)}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Start date:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            {new Date(term.startDate).toLocaleDateString()}
          </p>
        </div>
      </div>
    );
  }
};

const OfferCounterForm = ({
  offer,
  opening,
  closeFn,
}: {
  opening: CandidateJobOpening;
  offer?: Offer;
  closeFn: () => void;
}) => {
  if (!offer) {
    return;
  }

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const companyName = opening.company?.name || "the company";

  return (
    <>
      <SheetTitle className="pb-4">
        <div className="flex flex-row items-center gap-2">
          <ArrowLeft
            size={24}
            stroke="#2E2E38"
            className="cursor-pointer"
            onClick={closeFn}
          />
          Send a counter offer to {companyName}
        </div>
      </SheetTitle>
      <SheetDescription className="sr-only">
        Submit a counter offer for an opportunity
      </SheetDescription>

      <ScrollArea className="h-[calc(100vh-4.75rem)]" type="auto">
        <OpportunityCounterOfferForm
          jobOpening={opening}
          candidate={opening.candidateStatus}
          offer={offer}
          onCancel={closeFn}
          onSuccess={closeFn}
        />
      </ScrollArea>
    </>
  );
};

const Tile = ({
  className,
  children,
}: {
  className?: string;
  children?: ReactNode;
}) => {
  return (
    <div
      className={cn(
        "p-4 flex flex-col justify-center items-start gap-2 self-stretch bg-white",
        "border border-[#e5e5e5] rounded-lg",
        className,
      )}
    >
      {children}
    </div>
  );
};

const LinkButton = ({
  Icon,
  text,
  link,
}: {
  Icon: LucideIcon;
  text: string;
  link?: string;
}) => {
  if (!link) {
    return;
  }

  return (
    <Button
      variant="outline"
      onClick={() =>
        window.open(
          link.startsWith("http") ? link : `https://${link}`,
          "_blank",
        )
      }
    >
      <Icon />
      {text}
    </Button>
  );
};

const BackgroundItem = ({
  Icon,
  name,
  value,
}: {
  Icon: IconProp;
  name: string;
  value?: string;
}) => {
  if (!value) {
    return;
  }

  return (
    <div className="flex gap-3 self-stretch justify-between py-4 border-b border-[#F7F7F7] first:pt-0 last:pb-0 last:border-b-0">
      <div className="flex gap-3">
        <Icon size={20} />
        <p className="text-sm font-medium text-[#181D27]">{name}</p>
      </div>
      <p className="text-sm font-medium text-[#181D27]">{value}</p>
    </div>
  );
};

const OpportunityKeyPoint = ({
  Icon,
  value,
}: {
  Icon: IconProp;
  value?: string;
}) => {
  if (!value) {
    return;
  }

  return (
    <div className="flex flex-row gap-2 items-center">
      <Icon className="stroke-muted-foreground" size={16} />
      <span className="text-sm text-muted-foreground">{value}</span>
    </div>
  );
};
